const BASE = process.env.REACT_APP_API_URL || '';

async function request(path, options = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${BASE}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!res.ok) {
    // Token หมดอายุ → ล้าง session แล้ว reload ไปหน้า login
    if (res.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('agent');
      window.location.reload();
      return;
    }
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const e = new Error(err.message || 'Request failed');
    e.details = err.details;
    e.lineStatus = err.lineStatus;
    e.status = res.status;
    throw e;
  }
  return res.json();
}

// Auth
export const login = (email, password) =>
  request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
export const getMe = () => request('/auth/me');

// Agent self-service profile (any agent can use)
export const getAgentProfile    = ()    => request('/agent-profile');
export const updateAgentProfile = (data) => request('/agent-profile', { method: 'PUT', body: JSON.stringify(data) });

// OA Accounts
export const getOAAccounts = () => request('/oa');
export const createOAAccount = (data) => request('/oa', { method: 'POST', body: JSON.stringify(data) });
export const updateOAAccount = (id, data) => request(`/oa/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteOAAccount = (id) => request(`/oa/${id}`, { method: 'DELETE' });

// V2.8: LINE Bot operations (admin only)
export const getOaQuota         = (oaId)      => request(`/oa/${oaId}/quota`);
export const broadcastMessage   = (oaId, data) => request(`/oa/${oaId}/broadcast`, { method: 'POST', body: JSON.stringify(data) });
export const getRichMenus       = (oaId)      => request(`/oa/${oaId}/rich-menus`);
export const createRichMenu     = (oaId, data) => request(`/oa/${oaId}/rich-menus`, { method: 'POST', body: JSON.stringify(data) });
export const deleteRichMenu     = (oaId, rmId) => request(`/oa/${oaId}/rich-menus/${rmId}`, { method: 'DELETE' });
export const setDefaultRichMenu = (oaId, rmId) => request(`/oa/${oaId}/rich-menus/${rmId}/default`, { method: 'PUT' });
export const uploadRichMenuImage = (oaId, rmId, file) => {
  const token = localStorage.getItem('token');
  const fd = new FormData();
  fd.append('image', file);
  return fetch(`${BASE}/api/oa/${oaId}/rich-menus/${rmId}/image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  }).then(async res => {
    if (!res.ok) { const e = await res.json().catch(() => ({ message: res.statusText })); throw new Error(e.message); }
    return res.json();
  });
};
export const sendFlexMessage    = (convId, data) => request(`/conversations/${convId}/messages/flex`, { method: 'POST', body: JSON.stringify(data) });

// Conversations
export const getConversations = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return request(`/conversations${q ? `?${q}` : ''}`);
};

export const getMessages = (convId, params = {}) => {
  const q = new URLSearchParams(params).toString();
  return request(`/conversations/${convId}/messages${q ? `?${q}` : ''}`);
};

export const sendMessage = (convId, text) =>
  request(`/conversations/${convId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });

export const sendSticker = (convId, packageId, stickerId) =>
  request(`/conversations/${convId}/messages/sticker`, {
    method: 'POST',
    body: JSON.stringify({ packageId, stickerId }),
  });

export const sendImage = (convId, file) => {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('image', file);
  return fetch(`${BASE}/api/conversations/${convId}/messages/image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  }).then(async res => {
    if (res.status === 401) { localStorage.removeItem('token'); window.location.reload(); return; }
    if (!res.ok) { const e = await res.json().catch(() => ({ message: res.statusText })); throw new Error(e.message); }
    return res.json();
  });
};

export const createNote = (convId, text) =>
  request(`/conversations/${convId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });

export const assignAgent = (convId, agentId) =>
  request(`/conversations/${convId}/assign`, {
    method: 'PUT',
    body: JSON.stringify({ agent_id: agentId }),
  });

export const updateStatus = (convId, status) =>
  request(`/conversations/${convId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });

// Agents
export const getAgents = () => request('/agents');
export const createAgent = (data) => request('/agents', { method: 'POST', body: JSON.stringify(data) });
export const updateAgent = (id, data) => request(`/agents/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAgent = (id) => request(`/agents/${id}`, { method: 'DELETE' });

// OA Permissions (admin only)
export const getAgentOaPermissions = (agentId) => request(`/agents/${agentId}/oa-permissions`);
export const setAgentOaPermissions = (agentId, oaIds) =>
  request(`/agents/${agentId}/oa-permissions`, { method: 'PUT', body: JSON.stringify({ oa_ids: oaIds }) });

// Dashboard
export const getDashboard = ({ days = 7, oaId = null, startDate = null } = {}) => {
  const params = new URLSearchParams({ days });
  if (oaId) params.set('oaId', oaId);
  if (startDate) params.set('startDate', startDate);
  return request(`/dashboard?${params}`);
};

// Reports
export const getAgentReport = (days = 30) => request(`/reports/agent-performance?days=${days}`);

// Member Sync
export const syncMember = (convId) => request(`/conversations/${convId}/sync-member`, { method: 'POST' });

// Media
export const getConversationMedia = (convId) => request(`/conversations/${convId}/media`);

// Customer Info
export const getCustomerInfo   = (convId)       => request(`/conversations/${convId}/customer-info`);
export const updateCustomerInfo = (convId, data) => request(`/conversations/${convId}/customer-info`, { method: 'PUT', body: JSON.stringify(data) });
export const importCustomerInfo = (convId, data) => request(`/conversations/${convId}/customer-info/import`, { method: 'POST', body: JSON.stringify(data) });
export const addBankAccount    = (convId, data)  => request(`/conversations/${convId}/bank-accounts`, { method: 'POST', body: JSON.stringify(data) });
export const deleteBankAccount = (convId, bankId)=> request(`/conversations/${convId}/bank-accounts/${bankId}`, { method: 'DELETE' });

// Bot Shadow Logs (review)
export const getBotShadowLogs = (oaId, params = {}) => {
  const q = new URLSearchParams(params).toString();
  return request(`/oa/${oaId}/bot-shadow-logs${q ? `?${q}` : ''}`);
};

// Bot FAQs
export const getBotFaqs   = (oaId)       => request(`/oa/${oaId}/bot-faqs`);
export const createBotFaq = (oaId, data) => request(`/oa/${oaId}/bot-faqs`, { method: 'POST', body: JSON.stringify(data) });
export const updateBotFaq = (oaId, id, data) => request(`/oa/${oaId}/bot-faqs/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteBotFaq = (oaId, id)   => request(`/oa/${oaId}/bot-faqs/${id}`, { method: 'DELETE' });
export const rateShadowLog = (logId, rating) => request(`/bot-shadow-logs/${logId}/rate`, { method: 'PUT', body: JSON.stringify({ rating }) });
export const getBotStats  = (oaId, days=7) => request(`/oa/${oaId}/bot-stats?days=${days}`);
export const getBotShadowSummary = (oaId, days=7) => request(`/oa/${oaId}/bot-shadow-summary?days=${days}`);

// System Config (Executive AI)
export const getSystemConfig    = ()     => request('/system-config');
export const updateSystemConfig = (data) => request('/system-config', { method: 'PUT', body: JSON.stringify(data) });

// Quick Replies
export const getQuickReplies = () => request('/quick-replies');
export const saveQuickReply = (keyNumber, data) =>
  request(`/quick-replies/${keyNumber}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteQuickReply = (keyNumber) =>
  request(`/quick-replies/${keyNumber}`, { method: 'DELETE' });

// Tags
export const getTags = () => request('/tags');
export const createTag = (data) => request('/tags', { method: 'POST', body: JSON.stringify(data) });
export const updateTag = (id, data) => request(`/tags/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTag = (id) => request(`/tags/${id}`, { method: 'DELETE' });

// Conversation Tags
export const addConversationTag = (convId, tagId) =>
  request(`/conversations/${convId}/tags/${tagId}`, { method: 'POST' });
export const removeConversationTag = (convId, tagId) =>
  request(`/conversations/${convId}/tags/${tagId}`, { method: 'DELETE' });
